import React, { useCallback, useMemo, useState } from "react";
import { useExternalNavigate } from "../hooks/useExternalNavigate";
import { SendGridClient } from "./client";
import {
  SendGridNavigateOptions,
  SendGridRouterContext,
  SendGridRouterContextInterface,
  SendGridSessionContext,
  SendGridSessionContextInterface,
  SendGridSetSessionOptions,
} from "./SendGridAuthContext";
import { useAccountRegionCookie } from "./useAccountRegionCookie";
import { useImpersonationCookie } from "./useImpersonationCookie";
import { useMakoToken } from "./useMakoToken";
import { useMFASettingIsVerified } from "./useMFASettingIsVerified";
import { usePasswordResetRequired } from "./usePasswordResetRequired";
import { seedRandomSessionIdentifier, useSessionIdentifier } from "./useSessionIdentifier";
import {
  Setup2FARequiredCheckpoint,
  useSetup2FARequired,
  useSetup2FARequiredCheckpoint,
} from "./useSetup2FARequired";

export interface SendGridSessionProviderOptions {
  children: React.ReactNode;
  context?: React.Context<SendGridSessionContextInterface>;
  makoToken?: string;
}

export const SendGridSessionProvider = (opts: SendGridSessionProviderOptions): JSX.Element => {
  const [client] = useState(() => new SendGridClient({}));
  const { children, context = SendGridSessionContext } = opts;
  const { makoToken, setMakoToken, removeMakoToken } = useMakoToken();
  const [setup2FARequired, setSetup2FARequired, removeSetup2FARequired] = useSetup2FARequired();
  const [mfaSettingIsVerified, setMFASettingIsVerified, removeMFASettingIsVerified] =
    useMFASettingIsVerified();
  const [, , removeSetup2FARequiredCheckpoint] = useSetup2FARequiredCheckpoint();
  const [passwordResetRequired, setPasswordResetRequired, removePasswordResetRequired] =
    usePasswordResetRequired();
  const [sessionIdentifier, setSessionIdentifier, removeSessionIdentifier] = useSessionIdentifier();
  const { accountRegion, setAccountRegionCookie, removeAccountRegionCookie } =
    useAccountRegionCookie();
  const { removeImpersonatingUsernameCookie } = useImpersonationCookie();

  const isAuthenticated = useMemo(() => {
    return !!makoToken;
  }, [makoToken]);

  const resetSessionIdentifier = useCallback(() => {
    // If session identifier is not set then go ahead and generate it and set it.
    if (!sessionIdentifier) {
      setSessionIdentifier(seedRandomSessionIdentifier());
    }
  }, [sessionIdentifier, setSessionIdentifier]);

  const setSession = useCallback(
    (opts: SendGridSetSessionOptions) => {
      const {
        token,
        accountRegion,
        setup2FARequired = false,
        passwordResetRequired = false,
        mfaSettingIsVerified = false,
      } = opts;
      setMakoToken(token);
      setAccountRegionCookie(accountRegion);
      resetSessionIdentifier();
      setSetup2FARequired(setup2FARequired);
      setPasswordResetRequired(passwordResetRequired);
      setMFASettingIsVerified(mfaSettingIsVerified);
    },
    [
      setMakoToken,
      setAccountRegionCookie,
      resetSessionIdentifier,
      setSetup2FARequired,
      setPasswordResetRequired,
      setMFASettingIsVerified,
    ]
  );

  const validateSession = useCallback(async () => {
    if (!makoToken || !accountRegion) {
      return false;
    }
    const result = await client.FetchTwoFASetting(makoToken, accountRegion);
    return result.success;
  }, [client, makoToken, accountRegion]);

  const clearSession = useCallback(() => {
    removePasswordResetRequired();
    removeSessionIdentifier();
    removeSetup2FARequired();
    removeSetup2FARequiredCheckpoint();
    removeMFASettingIsVerified();
    removeMakoToken();
    removeAccountRegionCookie();
    removeImpersonatingUsernameCookie();
  }, [
    removePasswordResetRequired,
    removeSessionIdentifier,
    removeSetup2FARequired,
    removeSetup2FARequiredCheckpoint,
    removeMFASettingIsVerified,
    removeMakoToken,
    removeAccountRegionCookie,
    removeImpersonatingUsernameCookie,
  ]);

  const navigationOptions = useMemo(() => {
    return {
      setup2FARequired,
      passwordResetRequired,
      mfaSettingIsVerified,
    };
  }, [mfaSettingIsVerified, passwordResetRequired, setup2FARequired]);

  const contextValue = useMemo(() => {
    return {
      isAuthenticated,
      makoToken,
      accountRegion,
      setSession,
      validateSession,
      clearSession,
      navigationOptions,
    };
  }, [
    isAuthenticated,
    makoToken,
    accountRegion,
    setSession,
    validateSession,
    clearSession,
    navigationOptions,
  ]);
  return <context.Provider value={contextValue}>{children}</context.Provider>;
};

export interface SendGridRouterProviderOptions {
  children: React.ReactNode;
  context?: React.Context<SendGridRouterContextInterface>;
}

export const SendGridRouterProvider = (opts: SendGridRouterProviderOptions): JSX.Element => {
  const { children, context = SendGridRouterContext } = opts;
  const [, setSetup2FARequiredCheckpoint] = useSetup2FARequiredCheckpoint();
  const externalNavigate = useExternalNavigate();

  const navigate = useCallback(
    (opt: SendGridNavigateOptions) => {
      const {
        setup2FARequired,
        mfaSettingIsVerified,
        passwordResetRequired,
        redirectToOAuth,
        skipSecurityCheck,
        needsOnboarding,
      } = opt;
      // In all error cases, either redirect to password reset (if required)
      // or redirect to the security checkup.
      // The security checkup will check the 2FA status again
      // and render the appropriate flow based on the result.
      if (setup2FARequired) {
        setSetup2FARequiredCheckpoint(Setup2FARequiredCheckpoint.EMAIL);
        externalNavigate.toSetup2FARequiredEmailCheckpoint();
        return;
      } else if (mfaSettingIsVerified) {
        externalNavigate.toValidate2FA();
        return;
      } else if (passwordResetRequired) {
        externalNavigate.toPasswordReset();
        return;
      } else if (redirectToOAuth) {
        externalNavigate.toOAuthAuthorize();
        return;
      } else if (needsOnboarding) {
        externalNavigate.toOnboarding();
        return;
      } else if (skipSecurityCheck) {
        externalNavigate.toDashboard();
        return;
      } else {
        externalNavigate.toSecurityCheckup();
        return;
      }
    },
    [externalNavigate, setSetup2FARequiredCheckpoint]
  );

  const contextValue = useMemo(() => {
    return {
      navigate,
    };
  }, [navigate]);
  return <context.Provider value={contextValue}>{children}</context.Provider>;
};
